import { Link } from 'react-router-dom';
import './compact-band.css';

/**
 * Compact band for RCM work pages (00-ds-08-compact-band.md, D145). One green
 * line (64px), or two with tabs (106px), in place of the tall white page
 * header. The dark app title bar already names the page, so there is no kicker.
 *
 * Used by Human in the loop, Claims to review, Insurance A/R, ELVA's work,
 * Denied & underpaid, Posting, Pre-auths and Document Center — all look the
 * same. Classes are prefixed `cbd-` so they never collide with a feature's own
 * scoped CSS.
 */
export function CompactBand({
  headline,
  mint,
  right,
  tabs,
  tabsRight,
  loading,
}: {
  /** The headline sentence; wrap the highlighted phrase in <em>. */
  headline?: React.ReactNode;
  /** em colour: mint (good news) instead of the default amber (needs a person). */
  mint?: boolean;
  /** Right side of the headline row (links, a status, small buttons). */
  right?: React.ReactNode;
  /** Left side of the optional tabs row (BandTab list). */
  tabs?: React.ReactNode;
  /** Right side of the tabs row (filters or the page's controls). */
  tabsRight?: React.ReactNode;
  loading?: boolean;
}) {
  const hasTabs = tabs != null || tabsRight != null;
  return (
    <header className="cbd-band">
      <div className="cbd-cb">
        {loading ? (
          <div className="cbd-skel" aria-hidden />
        ) : (
          <h1 className={`cbd-h1${mint ? ' mint' : ''}`}>{headline}</h1>
        )}
        {right ? <div className="cbd-cr">{right}</div> : null}
      </div>
      {hasTabs ? (
        <div className="cbd-tabs">
          <div className="cbd-tabs-left" role="tablist">
            {tabs}
          </div>
          {tabsRight ? <div className="cbd-cr">{tabsRight}</div> : null}
        </div>
      ) : null}
    </header>
  );
}

/** A tab in the tabs row. `to` renders a Link; otherwise a button with onClick. */
export function BandTab({
  label,
  meta,
  active,
  to,
  onClick,
}: {
  label: string;
  /** The count and its words, shown in mono after the label ("5 · $3,983"). */
  meta?: React.ReactNode;
  active?: boolean;
  to?: string;
  onClick?: () => void;
}) {
  const inner = (
    <>
      {label}
      {meta != null ? <span className="m">{meta}</span> : null}
    </>
  );
  const cls = `cbd-t${active ? ' on' : ''}`;
  if (to) {
    return (
      <Link to={to} className={cls} role="tab" aria-selected={active ?? false} aria-current={active ? 'page' : undefined}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} role="tab" aria-selected={active ?? false} aria-current={active ? 'page' : undefined} onClick={onClick}>
      {inner}
    </button>
  );
}

/** A plain text link to another page: white 600 label + a mono count after it. */
export function BandLink({ to, label, count }: { to: string; label: string; count?: React.ReactNode }) {
  return (
    <Link to={to} className="cbd-cl">
      {label}
      {count != null ? <span className="m">{count}</span> : null}
    </Link>
  );
}

/** The `·` separator between band links. */
export function BandSep() {
  return <span className="cbd-sep" aria-hidden>·</span>;
}

/** A link to settings or help: mint, underlined. */
export function BandMintLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="cbd-lk">
      {children}
    </Link>
  );
}

/** A small band button. `w` = the main one (white), `g` = secondary. */
export function BandButton({
  children,
  variant = 'w',
  to,
  onClick,
}: {
  children: React.ReactNode;
  variant?: 'w' | 'g';
  to?: string;
  onClick?: () => void;
}) {
  const cls = `cbd-bbs ${variant}`;
  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      {children}
    </button>
  );
}

/** "Training ELVA" {confirmed} of {goal} + an 88×4 mint bar (no %). */
export function TrainBar({ confirmed, goal }: { confirmed: number; goal: number }) {
  const pct = Math.min(100, Math.round((confirmed / goal) * 100));
  return (
    <span className="cbd-tbar">
      <span>Training ELVA</span>
      <b className="cbd-tnum">
        {confirmed} of {goal}
      </b>
      <i aria-hidden>
        <b style={{ width: `${String(pct)}%` }} />
      </i>
    </span>
  );
}
